import { Routes, Route, Navigate } from "react-router";
// lazyWithReload is React.lazy plus one-shot auto-reload when a route's hashed
// chunk 404s after a deploy (s3 --delete removed the old chunk a stale tab still
// references). Aliased to `lazy` so the route declarations below read normally.
import { lazyWithReload as lazy } from "./lib/chunkReload";
// Chrome stays EAGER — the layout frames, the auth shell, and the first screens
// a cold load can land on (login / landing / OAuth callback / invite accept) and
// the always-reachable fallbacks (NotFound, GalaxyRedirect) must paint without a
// second network round-trip. Everything that renders INSIDE a layout's <Outlet/>
// is split out below via React.lazy so the initial bundle is "chrome + first
// screen", not the whole app. Each lazy import maps 1:1 to its own Vite chunk,
// auto-named after the component file (Observatory-[hash].js, …) — the "named
// chunk" the routing manifest asks for. See frontend-manifest §12.
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { ServerWakingOverlay } from "./components/ServerWakingOverlay";
import GalaxyRedirect from "./components/GalaxyRedirect";
import ArchiveLayout from "./components/ArchiveLayout";
import CareerLayout from "./components/CareerLayout";
import Landing from "./features/landing/Landing";
import Login from "./features/auth/Login";
import Callback from "./features/auth/Callback";
import InviteAccept from "./features/invite/InviteAccept";
import { NotFound } from "./features/not-found/NotFound";

// Lazy galaxy-scoped pages (rendered inside Layout's <Outlet/>). Default exports
// load directly; the named-export Members page is adapted to the { default }
// shape lazy() expects (NotFound stays eager as the catch-all fallback).
const Observatory = lazy(() => import("./features/observatory/Observatory"));
const DailyObservation = lazy(() => import("./features/daily/DailyObservation"));
const Ambient = lazy(() => import("./features/ambient/Ambient"));
const Constellation = lazy(() => import("./features/constellation/Constellation"));
const ConstellationFull = lazy(() => import("./features/constellation/ConstellationFull"));
const StarDetail = lazy(() => import("./features/star-detail/StarDetail"));
const Repos = lazy(() => import("./features/repos/Repos"));
const Team = lazy(() => import("./features/team/Team"));
const Settings = lazy(() => import("./features/settings/Settings"));
const SlackConnectorPage = lazy(() => import("./features/connectors/SlackConnectorPage"));
const Members = lazy(() =>
  import("./features/members/Members").then((m) => ({ default: m.Members })),
);
const ControlRoom = lazy(() => import("./features/control-room/ControlRoom"));
const Plan = lazy(() => import("./features/plan/Plan"));
const DisclosureSettings = lazy(() => import("./features/disclosure-settings/DisclosureSettings"));
const VisibilityMode = lazy(() => import("./features/visibility-mode/VisibilityMode"));
const Preferences = lazy(() => import("./features/preferences/Preferences"));
const ApiTokens = lazy(() => import("./features/api-tokens/ApiTokens"));
const McpIntegration = lazy(() => import("./features/mcp/McpIntegration"));
const Modules = lazy(() => import("./features/modules/Modules"));
const CollapseRiskRoute = lazy(() => import("./features/modules/CollapseRiskRoute"));
const Logbook = lazy(() => import("./features/timeline/Logbook"));
const ObservationRuns = lazy(() => import("./features/observation-runs/ObservationRuns"));
const Embeds = lazy(() => import("./features/embeds/Embeds"));
const FeedbackTickets = lazy(() => import("./features/feedback/FeedbackTickets"));
const TicketThread = lazy(() => import("./features/feedback/TicketThread"));
const OssPresence = lazy(() => import("./features/oss-presence/OssPresence"));
const GravityCard = lazy(() => import("./features/gravity-card/GravityCard"));

// Lazy archive/career pages (rendered inside ArchiveLayout / CareerLayout).
const AffiliationRecord = lazy(() => import("./features/affiliations/AffiliationRecord"));
const AffiliationCards = lazy(() => import("./features/affiliations/AffiliationCards"));
const CareerPage = lazy(() => import("./features/career/CareerPage"));

export default function App() {
  return (
    <>
      {/* Global: covers any page when the whole backend is unreachable (stg idle
          scale-to-0 / a real outage) — confirms via /health, reconnects, reloads. */}
      <ServerWakingOverlay />
      <Routes>
      <Route path="/lp" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route path="/invite/:code" element={<InviteAccept />} />
      <Route element={<ProtectedRoute />}>
        {/* Root → redirect to /:default-account/ */}
        <Route path="/" element={<GalaxyRedirect />} />
        {/* Galaxy-scoped routes: /:account/... */}
        <Route path="/:account" element={<Layout />}>
          <Route index element={<Observatory />} />
          <Route path="daily" element={<DailyObservation />} />
          {/* Ambient: the always-on / standup READ surface (wall + standup modes;
              docs/ace/ambient-display.md, BE #980). Read-only, no-operation,
              auto-refreshing — structural facts only. ?team= selects the standup
              (team) scope; absent = the galaxy/wall scope. */}
          <Route path="ambient" element={<Ambient />} />
          <Route path="constellation" element={<Constellation />} />
          <Route path="constellation/full" element={<ConstellationFull />} />
          <Route path="member/:author" element={<StarDetail />} />
          <Route path="repos" element={<Repos />} />
          {/* Team and Career are both GA (default-on), reachable in every env. */}
          <Route path="teams" element={<Team />} />
          <Route path="modules" element={<Modules />} />
          {/* Dedicated Collapse Risk triage view (drill-in from the Modules
              summary card). FE-gated to paid plans inside the page; backend is
              plan-independent — it reuses /modules/ownership, no new endpoint. */}
          <Route path="modules/collapse-risk" element={<CollapseRiskRoute />} />
          <Route path="logbook" element={<Logbook />} />
          <Route path="runs" element={<ObservationRuns />} />
          <Route path="embeds" element={<Embeds />} />
          {/* Career MOVED to /me/career (user-scoped, its own minimal chrome) —
              its data was always keyed off the JWT, never the :account in the
              URL, so the galaxy-framed URL lied about scope. This legacy redirect
              keeps old links alive (bookmarks, the #1061 StarSelfShare CTA, any
              external link) — :account is dropped because career is a property of
              the PERSON, not the galaxy. */}
          <Route path="career" element={<Navigate to="/me/career" replace />} />
          <Route path="control-room" element={<ControlRoom />} />
          <Route path="plan" element={<Plan />} />
          {/* Members is org-admin only; the component itself redirects others
              back to the Control Room. */}
          <Route path="members" element={<Members />} />
          <Route path="settings" element={<Settings />} />
          {/* Passive Connector (Slack) — its own destination, split out of the
              EIS Settings page so it can carry the full how-to / what-it-won't-do
              explanation (docs/ace/passive-connector.md). The OAuth callback
              redirects the browser here with a #slack=connected / #error
              fragment. FE-gated to paid plans inside the page (Pro/Nova by
              account_type); galaxy is account-scoped from useParams. */}
          <Route path="connectors/slack" element={<SlackConnectorPage />} />
          <Route path="disclosure" element={<DisclosureSettings />} />
          <Route path="visibility-mode" element={<VisibilityMode />} />
          {/* Language + time zone — personal display (moved off the EIS Config
              page) + the galaxy's output language (moved off Visibility Mode). */}
          <Route path="preferences" element={<Preferences />} />
          {/* API tokens — eis_ CLI upload keys (moved off the EIS Config page). */}
          <Route path="api-tokens" element={<ApiTokens />} />
          {/* AI Integration — mcp_ tokens for the OrbitLens MCP context server
              (read-only tools for an AI coding agent) + usage + connect how-to.
              The backend endpoints are adminOnly and enforce it server-side;
              the Sidebar nav row is admin-gated (isAccountAdmin) and the page
              itself shows an admin-only notice as defense in depth for a
              direct URL hit — same pattern as VisibilityMode / Members. */}
          <Route path="mcp" element={<McpIntegration />} />
          {/* OSS research participation (user-scoped): whether a PUBLISHED artifact
              may name you. Broadcast-only — never changes the in-product view. */}
          <Route path="oss-presence" element={<OssPresence />} />
          {/* Personal Gravity Card — the caller's own self-observation
              (archetype histogram + footprint). User-scoped (JWT); :account is
              nav context only. The acquisition-wedge surface. */}
          <Route path="gravity-card" element={<GravityCard />} />
          {/* Feedback tickets (user ↔ staff). User-scoped, not galaxy-scoped —
              :account is here only for nav/URL consistency. */}
          <Route path="feedback" element={<FeedbackTickets />} />
          <Route path="feedback/:id" element={<TicketThread />} />
        </Route>
        {/* Affiliation record: a galaxy you've LEFT, with your gravity frozen at
            departure. Rendered in ArchiveLayout (a minimal, non-operational
            chrome) — NOT the galaxy Layout, because a departed galaxy can't be
            observed, so its operational nav must not appear. The record is
            user-scoped (meApi.affiliation, JWT-keyed): :galaxyId is the archived
            galaxy and there is no :account — the data is keyed off the JWT, not
            a live account you can switch into. */}
        <Route path="/me/affiliations/:galaxyId" element={<ArchiveLayout />}>
          <Route index element={<AffiliationRecord />} />
          <Route path="cards" element={<AffiliationCards />} />
        </Route>
        {/* Career Orbit: a person's trajectory gathered ACROSS every galaxy they
            belonged to, plus the certificates they've sealed. User-scoped and
            JWT-keyed (CareerOrbit reads /me/* — career/*, affiliations* — never an
            :account), so it lives under /me/* with its own minimal, non-operational
            chrome (CareerLayout) instead of a galaxy's operational Layout. The URL
            now states the truth — /me/career = "my career" — so the page no longer
            needs an on-screen "this is your own record, not the galaxy's" note.
            Entry point is the identity (account) menu. */}
        <Route path="/me/career" element={<CareerLayout />}>
          <Route index element={<CareerPage />} />
        </Route>
      </Route>
      {/* Catch-all: any URL that matched no route above. Without this an unknown
          path fell through <Routes> and rendered a blank white screen. NotFound
          is public (no API / no galaxy context) so it sits OUTSIDE ProtectedRoute
          — a bad URL shouldn't bounce through login just to say "not found". */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
